import React from "react";

/**
 * ResultText.jsx
 * - Robust mapping from DB result lines -> icon+link cards
 * - Handles "dirty" DB strings: extra spaces, punctuation, quotes, RTL marks, Arabic diacritics, etc.
 * - Works with Hebrew + Arabic (pass lang="he" | "ar")
 */

// ----------- helpers (normalization) -----------

const RTL_INVISIBLES_REGEX = /[\u200E\u200F\u061C\u202A-\u202E\u2066-\u2069]/g;
const ARABIC_DIACRITICS_REGEX = /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED]/g;

function normalizeLine(raw) {
    if (!raw) return "";
    return String(raw)
        .replace(RTL_INVISIBLES_REGEX, "")
        .replace(ARABIC_DIACRITICS_REGEX, "")
        .replace(/\u00A0/g, " ")
        .replace(/[“”„"]/g, "")
        .replace(/[’׳']/g, "")
        .replace(/[.,;:!؟…·•]/g, "")
        .replace(/[()[\]{}]/g, " ")
        .replace(/[/\\]/g, " / ")
        .replace(/[-–—]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
}

function splitLines(text) {
    return String(text || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
}

// ----------- icon/link catalog -----------

const METHODS = {
    condom: {
        he: { title: "קונדום", aliases: ["קונדום", "קונדומים"] },
        ar: { title: "الواقي الذكري", aliases: ["الواقي الذكري", "واقي ذكري", "الواقي"] },
        icon: "/icons/002-condom.png",
        link: "https://www.wtb.org.il/condom/",
    },
    progestin_pills: {
        he: { title: "גלולות פרוגסטרון", aliases: ["גלולות פרוגסטרון", "גלולת פרוגסטרון", "מיני פיל", "מיניפיל"] },
        ar: { title: "حبوب البروجستيرون", aliases: ["حبوب البروجستيرون", "حبوب بروجيستيرون", "حبوب منع الحمل (بروجستيرون)", "حبوب منع الحمل بروجستيرون", "حبوب البروجسترون"] },
        icon: "/icons/009-birth-control-pills.png",
        link: "https://www.wtb.org.il/birth-control-pills/",
    },
    combined_pills: {
        he: { title: "גלולות משולבות", aliases: ["גלולות משולבות", "גלולות משולבות למניעת הריון"] },
        ar: { title: "حبوب مركبة", aliases: ["حبوب مركبة", "حبوب منع الحمل المركبة", "الحبوب المركّبة (إستروجين وبروجسترون)", "الحبوب المركبة"] },
        icon: "/icons/004-contraceptive-pills.png",
        link: "https://www.wtb.org.il/birth-control-pills/",
    },
    patch: {
        he: { title: "מדבקה עורית", aliases: ["מדבקה עורית", "מדבקה", "פאץ׳", "פאצ"] },
        ar: { title: "لاصقة جلدية", aliases: ["لاصقة جلدية", "لاصقة", "لاصقة منع الحمل", "اللاصقة الجلدية", "اللصقة الجلدية"] },
        icon: "/icons/contraceptive (2).png",
        link: "https://www.wtb.org.il/weekly-skin-patch/",
    },
    diaphragm: {
        he: { title: "דיאפרגמה וקוטל זרע", aliases: ["דיאפרגמה וקוטל זרע", "דיאפרגמה", "קוטל זרע"] },
        ar: { title: "الحاجز المهبلي ومبيد النطاف", aliases: ["الحاجز المهبلي", "مبيد النطاف", "الحاجز ومبيد النطاف", "حاجز مهبلي ومبيد النطاف"] },
        icon: "/icons/017-diaphragm.png",
        link: "https://www.wtb.org.il/diaphragm/",
    },
    shot: {
        he: { title: "זריקות פרוגסטרון למניעת היריון", aliases: ["זריקות פרוגסטרון למניעת היריון", "זריקות פרוגסטרון", "זריקות למניעת היריון", "זריקות למניעת הריון"] },
        ar: {
            title: "حقن البروجستيرون لمنع الحمل",
            aliases: ["حقن البروجستيرون لمنع الحمل", "حقن البروجيستيرون لمنع الحمل", "حقن لمنع الحمل", "حقنة منع الحمل", "حقن منع الحمل (بروجستيرون)", "حقن منع الحمل بروجستيرون", "حقن البروجسترون لمنع الحمل"],
        },
        icon: "/icons/011-syringe.png",
        link: "https://www.wtb.org.il/birth-control-shot/",
    },
    fertility_awareness: {
        he: { title: "שיטת המודעות לפוריות", aliases: ["שיטת המודעות לפוריות", "מודעות לפוריות", "מעקב פוריות", "שיטה טבעית"] },
        ar: { title: "طريقة الوعي بالخصوبة", aliases: ["طريقة الوعي بالخصوبة", "الوعي بالخصوبة", "متابعة الخصوبة"] },
        icon: "/icons/005-calendar.png",
        link: "https://www.wtb.org.il/fertility-awareness-method/",
    },
    hormonal_iud: {
        he: { title: "התקן הורמונלי", aliases: ["התקן הורמונלי", "כהתקן הורמונלי", "התקן הומונלי", "התקן תוך רחמי הורמונלי"] },
        ar: { title: "اللولب الهرموني", aliases: ["اللولب الهرموني", "لولب هرموني", "اللولب (هرموني)"] },
        icon: "/icons/012-iud-1.png",
        link: "https://www.wtb.org.il/hormonal-intrauterine-system",
    },
    copper_iud: {
        he: { title: "התקן נחושת", aliases: ["התקן נחושת", "התקן תוך רחמי נחושת"] },
        ar: { title: "اللولب النحاسي", aliases: ["اللولب النحاسي", "لولب نحاسي", "اللولب (نحاسي)"] },
        icon: "/icons/001-iud.png",
        link: "https://www.wtb.org.il/copper-intrauterine-device",
    },
    ring: {
        he: { title: "טבעת וגינאלית", aliases: ["טבעת וגינאלית", "טבעת וגינלית", "טבעת נרתיקית"] },
        ar: { title: "حلقة مهبلية", aliases: ["حلقة مهبلية", "الحلقة المهبلية", "حلقة منع الحمل"] },
        icon: "/icons/003-ring.png",
        link: "https://www.wtb.org.il/vaginal-ring/",
    },
    sponge: {
        he: { title: "ספוגית", aliases: ["ספוגית", "ספוג למניעת הריון"] },
        ar: { title: "إسفنجة", aliases: ["إسفنجة", "الإسفنجة", "إسفنجة منع الحمل", "الإسفنجة المهبلية"] },
        icon: "/icons/circle.png",
        link: "https://www.wtb.org.il/sponge/",
    },
    irreversible: {
        he: { title: "קשירת או כריתת חצוצרות/קשירת צינור זרע", aliases: ["קשירת או כריתת חצוצרות/קשירת צינור זרע", "קשירת חצוצרות", "כריתת חצוצרות", "קשירת צינור זרע", "ואזקטומי"] },
        ar: { title: "ربط/استئصال قناتي فالوب أو قطع القناة المنوية", aliases: ["ربط/استئصال قناتي فالوب أو قطع القناة المنوية", "ربط قناتي فالوب", "استئصال قناتي فالوب", "قطع القناة المنوية", "فازكتومي", "ربط أو استئصال قناتي فالوب / ربط القناة المنوية", "استئصال قناتي فالوب / ربط القناة المنوية", "ربط أو استئصال قناتي فالوب / ربط القناة المنوي"] },
        icon: "/icons/scalpel.png",
        link: "https://www.wtb.org.il/irreversible-contraceptives/",
    },
};

function buildIndex(lang) {
    const idx = [];
    Object.entries(METHODS).forEach(([methodId, m]) => {
        const pack = m[lang] || m.he;
        const title = pack?.title || "";
        const aliases = pack?.aliases || [];
        const tokens = [title, ...aliases].map(normalizeLine).filter(Boolean);

        idx.push({
            methodId,
            icon: m.icon,
            link: m.link,
            title: pack?.title || m.he?.title || methodId,
            tokens,
        });
    });
    return idx;
}
function escapeRegExp(str) {
    return String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function wordCount(s) {
    return String(s || "").trim().split(/\s+/).filter(Boolean).length;
}

function findMethodForLine(lineNormalized, index, rawLine) {
    if (!lineNormalized) return null;

    // 1) exact token match
    for (const item of index) {
        if (item.tokens.includes(lineNormalized)) return item;
    }

    // 2) "safe" partial match:
    //    רק לשורות קצרות, ורק אם הטוקן מופיע כמילה שלמה
    const wc = wordCount(rawLine);
    if (wc > 6) return null; // משפטים/פסקאות — לא עושים includes בכלל

    for (const item of index) {
        for (const tok of item.tokens) {
            if (!tok) continue;

            // מונע התאמות קצרות מדי כמו "الواقي" בתוך משפטים
            if (tok.length < 6) continue;

            const re = new RegExp(`(^|\\s)${escapeRegExp(tok)}(\\s|$)`, "i");
            if (re.test(lineNormalized)) return item;
        }
    }

    return null;
}

export default function ResultText({ text, lang = "he" }) {
    const lines = splitLines(text);
    const langKey = lang === "ar" ? "ar" : "he";
    const index = buildIndex(langKey);

    const isProbablyParagraph = (rawLine) => {
        const s = String(rawLine || "").trim();
        return s.length > 55 || /[.!?؟]/.test(s);
    };

    return (
        <div
            className="bg-white/70 rounded-xl px-6 py-6 max-w-2xl mx-auto text-darktext text-inherit"
            dir="rtl"
        >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full max-w-xl mx-auto">
                {lines.map((rawLine, indexKey) => {
                    const norm = normalizeLine(rawLine);
                    const matched = findMethodForLine(norm, index, rawLine);

                    if (matched) {
                        return (
                            <a
                                key={indexKey}
                                href={matched.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg hover:border-[#704B47] hover:bg-[#F6EDE9] hover:text-[#704B47] transition duration-200"
                            >
                                <img src={matched.icon} alt={`icon ${matched.title}`} className="w-8 h-8 mb-2" />
                                <span className="font-semibold text-center leading-relaxed">{rawLine}</span>
                            </a>
                        );
                    }

                    return (
                        <p
                            key={indexKey}
                            className={`text-center leading-relaxed col-span-full ${
                                isProbablyParagraph(rawLine)
                                    ? "font-normal"
                                    : "font-medium"
                            }`}
                        >
                            {rawLine}
                        </p>
                    );
                })}
            </div>
        </div>
    );
}